/**
 * Payout tracking for the planner.
 *
 * Two numbers, from two different places, and they are easy to confuse:
 *
 *  - **Ratio** — provider cost as a percentage of customer revenue. Computed by
 *    ac-experience from the HQ event (`activities[0].cost.adjustment / totalPrice`) and
 *    delivered on the booking as `payoutRatio`. HQ holds one revenue figure per event, and
 *    an occasion is one event, so this only ever exists at occasion level.
 *  - **Cost** — the sum of `stop.item.price`. This is per stop, so it can be totalled per
 *    component as well as per occasion, and it is what ac-experience pushes back to HQ as
 *    the experience price.
 *
 * A component therefore gets a cost and no ratio: there is no per-component revenue to
 * divide by, and splitting the occasion's total would produce a number that looks precise
 * and means nothing.
 */

/**
 * The unit-economics traffic light. Thresholds are per currency and are unchanged from
 * where they used to live inline in `planners/Booking.jsx` — delivery's KPI is a 40% payout
 * ratio, but the tracker's green band deliberately runs to 50%.
 *
 * Returns null for a currency it does not know, which is how it has always behaved.
 */
const getPayoutTrafficColor = (currencyCode, percentage) => {
  if (['AUD', 'NZD', 'INR'].includes(currencyCode)) {
    if (percentage <= 50) {
      return '#4caf50';
    } else if (percentage > 50 && percentage <= 55) {
      return 'Orange';
    }
    return 'Red';
  } else if (['USD', 'CAD', 'GBP'].includes(currencyCode)) {
    if (percentage <= 53) {
      return '#4caf50';
    } else if (percentage > 53 && percentage <= 58) {
      return 'Orange';
    }
    return 'Red';
  }
  return null; // Unknown currency
};

/** Whether a booking carries a payout ratio worth drawing. */
const hasPayoutRatio = (booking) => Number.isFinite(booking?.payoutRatio);

/**
 * The provider cost carried by a list of stops. Mirrors ac-experience's `totalPriceReducer`,
 * which is what actually reaches HQ — including its treatment of a missing or zero price as
 * nothing to add.
 */
const sumStopCosts = (stops = []) =>
  (stops || []).reduce((total, stop) => {
    const price = Number(stop?.item?.price);
    return Number.isFinite(price) ? total + price : total;
  }, 0);

/** One component booking's provider cost. */
const componentCost = (componentBooking) => sumStopCosts(componentBooking?.route?.stops);

/**
 * An occasion's provider cost: the union of its components', because an occasion has no
 * route of its own. Same rule as ac-experience's `getPricedStops`, so the figure shown here
 * is the one pushed to HQ.
 */
const occasionCost = (occasion) =>
  (occasion?.bookings || []).reduce(
    (total, component) => total + componentCost(component),
    0
  );

export { getPayoutTrafficColor, hasPayoutRatio, sumStopCosts, componentCost, occasionCost };
